import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { Logger } from '@saviynt/common';

import { TABS_FILTERS } from '../../../shared/constants';
import { getPersonlization, useInvertedLocalize } from '../../../shared/hooks';
import { type TabFilterState } from '../../../shared/types';
import { type GetTabCountApiResponse } from '../data-layer/violationsTabTypes';
import { violationsTabActions } from '../data-store/violationsTabSlice';

/**
 * Custom hook for managing tab filters in the violations tab
 * @returns {Object} An object containing filter initialization methods
 * @returns {Function} returns.initializeTabFilters - Function to initialize tab filters
 *
 * @example
 * const { initializeTabFilters } = useTabFilters();
 * const success = initializeTabFilters();
 */
const useTabFilters = () => {
  const l = useInvertedLocalize();
  const dispatch = useDispatch();
  const [fetchTabCount] = violationsTabActions.useLazyGetTabCountQuery();

  const defaultTabValue = getPersonlization()?.tab ?? null;

  /**
   * Initializes the tab filters with predefined filter values
   * @returns {boolean} Returns true if initialization is successful, false otherwise
   */
  const initializeTabFilters = useCallback(async () => {
    try {
      const response =
        (await fetchTabCount().unwrap()) as GetTabCountApiResponse;

      const tabCountArr: TabFilterState[] = Object.values(TABS_FILTERS)
        .map((tab) => ({
          ...tab,
          label: l(tab.label),
          count: Number(response[tab.value as keyof GetTabCountApiResponse]),
          isInitial: tab.value === defaultTabValue,
        }))
        .filter((tab) => tab.count !== 0 || !tab.isZeroBased);

      dispatch(violationsTabActions.setTabFilters(tabCountArr));

      return true;
    } catch (error) {
      Logger.log('Error fetching tab count', error);

      return false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- should be only one time for `defaultTab`
  }, [dispatch, fetchTabCount, l]);

  return { initializeTabFilters };
};

// eslint-disable-next-line import/prefer-default-export
export { useTabFilters };
